<template>
  <Transition name="modal">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 z-[9999]"
      @click.self="handleClose"
    >
      <div
        class="bg-white rounded-2xl my-4 shadow-[4px_4px_0_0_rgba(0,0,0,1)] border-2 border-black max-w-md w-full transform transition-all"
        @click.stop
      >
        <!-- Header -->
        <div class="flex items-center justify-between px-6 pt-4">
          <h2 class="text-2xl font-bold text-gray-900">Edit Education</h2>
          <button
            @click="handleClose"
            class="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <XMarkIcon class="w-6 h-6" />
          </button>
        </div>

        <!-- Content -->
        <div class="px-8 py-6 space-y-6 max-h-[60vh] overflow-y-auto">
          <!-- Loop through each education entry -->
          <div 
            v-for="(item, index) in localItems" 
            :key="index"
            class="space-y-4 pb-6 border-b-2 border-gray-200 last:border-b-0"
          >
            <!-- Header with remove button -->
            <div class="flex items-center justify-between">
              <h3 class="text-lg font-bold text-gray-900">
                Education {{ index + 1 }}
              </h3>
              <button
                v-if="localItems.length > 1"
                @click="removeItem(index)"
                class="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                title="Remove education"
              >
                <XMarkIcon class="w-5 h-5" />
              </button>
            </div>

            <!-- Degree Selection -->
            <div class="space-y-2">
              <label class="block text-sm font-semibold text-gray-700">
                Degree Level
              </label>
              <div class="relative">
                <select
                  v-model="item.degree"
                  class="w-full h-12 pl-10 pr-4 bg-white rounded-lg border-2 border-gray-200 appearance-none text-gray-900 focus:outline-none focus:border-blue-500 transition-colors"
                >
                  <option value="" disabled>Select degree level...</option>
                  <option v-for="degree in degrees" :key="degree" :value="degree">
                    {{ degree }}
                  </option>
                </select>
                <AcademicCapIcon class="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                <ChevronDownIcon class="absolute right-3 top-3.5 w-5 h-5 text-gray-400" />
              </div>
            </div>

            <!-- Institution Name -->
            <div class="space-y-2">
              <label class="block text-sm font-semibold text-gray-700">
                Institution Name
              </label>
              <div class="relative">
                <input
                  type="text"
                  v-model="item.school"
                  class="w-full h-12 pl-10 pr-4 bg-white rounded-lg border-2 border-gray-200 text-gray-900 focus:outline-none focus:border-blue-500 transition-colors"
                  placeholder="Enter institution name"
                />
                <BuildingLibraryIcon class="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
              </div>
            </div>

            <!-- Field of Study -->
            <div class="space-y-2">
              <label class="block text-sm font-semibold text-gray-700">
                Field of Study
              </label>
              <div class="relative">
                <input
                  type="text"
                  v-model="item.fieldOfStudy"
                  class="w-full h-12 pl-10 pr-4 bg-white rounded-lg border-2 border-gray-200 text-gray-900 focus:outline-none focus:border-blue-500 transition-colors"
                  placeholder="Enter field of study (e.g., Computer Science, Mathematics)"
                />
                <BookOpenIcon class="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
              </div>
            </div>

            <!-- Additional Details (Optional) -->
            <div class="space-y-2">
              <label class="block text-sm font-semibold text-gray-700">
                Additional Details
              </label>
              <div class="relative">
                <input
                  type="text"
                  v-model="item.details"
                  class="w-full h-12 pl-10 pr-4 bg-white rounded-lg border-2 border-gray-200 text-gray-900 focus:outline-none focus:border-blue-500 transition-colors"
                  placeholder="Year, Honors, GPA (Optional)"
                />
                <InformationCircleIcon class="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
              </div>
            </div>

            <!-- Preview for this education entry -->
            <div v-if="item.degree && item.school" class="p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg">
              <div class="flex items-start gap-3">
                <div class="flex-shrink-0 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                  <AcademicCapIcon class="w-5 h-5 text-white" />
                </div>
                <div>
                  <p class="text-xs font-semibold text-blue-900 mb-1">Preview</p>
                  <p class="text-sm font-bold text-blue-700">{{ item.degree }} - {{ item.school }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Add Another Education Button -->
          <button
            @click="addItem"
            class="w-full py-3 text-blue-600 bg-blue-50 border-2 border-blue-200 border-dashed rounded-lg hover:bg-blue-100 hover:border-blue-400 transition-colors font-medium flex items-center justify-center gap-2"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            Add Another Education
          </button>
        </div>

        <!-- Footer -->
        <div class="flex items-center justify-end gap-3 p-6">
          <button
            @click="handleClose"
            class="px-6 py-2.5 text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            @click="handleSave"
            :disabled="!isValid || isSaving"
            class="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium shadow-md"
          >
            {{ isSaving ? 'Saving...' : 'Save Changes' }}
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { 
  XMarkIcon,
  AcademicCapIcon,
  BuildingLibraryIcon,
  InformationCircleIcon,
  ChevronDownIcon,
  BookOpenIcon
} from '@heroicons/vue/24/outline';
import type { EducationItem } from '@/types/profile';

interface Props {
  isOpen: boolean;
  items: EducationItem[];
  // Additional props for individual fields from registration
  institution?: string;
  fieldOfStudy?: string;
  educationLevel?: string;
  graduationYear?: number;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'save', items: EducationItem[]): void;
}>();

const degrees = computed(() => {
  const baseDegrees = [
    'Bachelor of Science',
    'Bachelor of Arts',
    'Master of Science',
    'Master of Arts',
    'Doctor of Philosophy',
    'Associate Degree',
    'High School Diploma',
    'Vocational Certificate',
    'Other'
  ];
  
  // If we have an educationLevel that doesn't match our predefined options,
  // add it to the list so it can be selected
  if (props.educationLevel) {
    const trimmedLevel = props.educationLevel.trim();
    if (!baseDegrees.includes(trimmedLevel)) {
      return [trimmedLevel, ...baseDegrees];
    }
  }
  
  return baseDegrees;
});

const localItems = ref<EducationItem[]>(props.items?.map(item => ({ ...item })) || [{
  degree: '',
  school: '',
  fieldOfStudy: '',
  details: ''
}]);
const isSaving = ref(false);

const isValid = computed(() => {
  return localItems.value.every(item => 
    item.degree.trim().length > 0 && 
    item.school.trim().length > 0
  );
});

// Compute graduation status text based on year
const getGraduationText = (year: number | undefined): string => {
  if (!year) return '';
  
  const currentYear = new Date().getFullYear();
  
  if (year < currentYear) {
    return `Graduated ${year}`;
  } else if (year === currentYear) {
    return `Expected year of graduation ${year}`;
  } else {
    return `Expected year of graduation ${year}`;
  }
};

watch(() => props.isOpen, (newVal) => {
  if (newVal) {
    if (props.items?.length) {
      // Use existing education items
      localItems.value = props.items.map(item => ({ ...item }));
    } else {
      // Create first education entry from individual fields
      // Map educationLevel to a degree that matches our dropdown options
      const mappedDegree = mapEducationLevelToDegree(props.educationLevel);
      
      const firstEducation: EducationItem = {
        degree: mappedDegree,
        school: props.institution || '',
        fieldOfStudy: props.fieldOfStudy || '',
        details: getGraduationText(props.graduationYear)
      };
      localItems.value = [firstEducation];
    }
  }
});

// Function to map educationLevel from Firebase to our degree options
const mapEducationLevelToDegree = (educationLevel: string | undefined): string => {
  if (!educationLevel) return '';
  
  // Trim whitespace and convert to lowercase for comparison
  const level = educationLevel.trim().toLowerCase();
  
  // Map common education level values to our dropdown options
  if (level.includes('bachelor') && level.includes('science')) return 'Bachelor of Science';
  if (level.includes('bachelor') && level.includes('arts')) return 'Bachelor of Arts';
  if (level.includes('master') && level.includes('science')) return 'Master of Science';
  if (level.includes('master') && level.includes('arts')) return 'Master of Arts';
  if (level.includes('doctor') || level.includes('phd')) return 'Doctor of Philosophy';
  if (level.includes('associate')) return 'Associate Degree';
  if (level.includes('high school')) return 'High School Diploma';
  if (level.includes('vocational') || level.includes('certificate')) return 'Vocational Certificate';
  
  // If it doesn't match any of our options, return the trimmed original value
  // This will show in the dropdown as a custom option
  return educationLevel.trim();
};

const addItem = () => {
  localItems.value.push({
    degree: '',
    school: '',
    fieldOfStudy: '',
    details: ''
  });
};

const removeItem = (index: number) => {
  localItems.value.splice(index, 1);
};

const handleSave = () => {
  if (!isValid.value) return;
  
  isSaving.value = true;
  const validItems = localItems.value.filter(item => 
    item.degree.trim().length > 0 && 
    item.school.trim().length > 0
  );
  emit('save', validItems);
  isSaving.value = false;
};

const handleClose = () => {
  emit('close');
};
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .bg-white,
.modal-leave-active .bg-white {
  transition: transform 0.3s ease;
}

.modal-enter-from .bg-white,
.modal-leave-to .bg-white {
  transform: scale(0.9);
}
</style>